/**
 * Thin API client. In dev, "/api" is proxied to the backend by Vite; in
 * production Apache proxies the same prefix, so no base URL is baked in.
 */
import { getLang, tr } from "./lib/i18n.jsx";

const BASE = import.meta.env.VITE_API_BASE ?? "/api";

// Venue versions (item 0375). The public API serves the PUBLISHED version.
// An admin's "Preview" opens the viewer with ?preview=<token>, and every venue
// request then carries it, so the whole page shows that one version.
const PREVIEW = new URLSearchParams(window.location.search).get("preview");
export const previewToken = () => PREVIEW;

// The published version this page booted with, from the venue bundle. Data
// requests carry it as ?v= (cache-busting), and screens compare it with
// /version to know a new one was published.
let bootVersion = null;
export const getBootVersion = () => bootVersion;

function venuePath(path) {
  if (!path.startsWith("/venues/")) return path;
  const params = new URLSearchParams();
  if (PREVIEW) params.set("preview", PREVIEW);
  else if (bootVersion?.id) params.set("v", bootVersion.id);
  const qs = params.toString();
  if (!qs) return path;
  return path + (path.includes("?") ? "&" : "?") + qs;
}

class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

async function request(path, options = {}) {
  let response;
  try {
    response = await fetch(`${BASE}${venuePath(path)}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        // The backend localizes its own strings (route errors) off this header.
        "Accept-Language": getLang(),
        ...options.headers,
      },
    });
  } catch {
    throw new ApiError(tr("err_network"), 0);
  }

  if (response.status === 204) return null;
  const body = await response.json().catch(() => null);

  if (!response.ok) {
    const detail = body?.detail;
    // 3D Maps is a paid RDS add-on: the backend answers 402 with a structured
    // detail when the venue's company is no longer entitled. Surface the code
    // so the viewer can show a proper notice instead of a raw error string.
    if (response.status === 402 && detail?.code === "maps3d_not_entitled") {
      const blocked = new ApiError(tr("err_maps3d_title"), 402);
      blocked.code = detail.code;
      throw blocked;
    }
    const message =
      typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => d.msg).join(", ")
          : `Request failed (${response.status})`;
    const error = new ApiError(message, response.status);
    // Structured errors ({"code": ...}, e.g. the assistant's) keep their code.
    if (detail && typeof detail === "object" && !Array.isArray(detail)) error.code = detail.code;
    throw error;
  }
  return body;
}

export const api = {
  venue: (slug) =>
    request(`/venues/${encodeURIComponent(slug)}`).then((bundle) => {
      bootVersion = bundle?.version ?? null;
      return bundle;
    }),
  // What is live now, in a few bytes (no ?v=, no preview: always the truth).
  version: (slug) =>
    fetch(`${BASE}/venues/${encodeURIComponent(slug)}/version`, { cache: "no-store" }).then((r) =>
      r.ok ? r.json() : null,
    ),
  features: (slug) => request(`/venues/${encodeURIComponent(slug)}/features`),
  pois: (slug, { category, q } = {}) => {
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (q) params.set("q", q);
    const qs = params.toString();
    return request(`/venues/${encodeURIComponent(slug)}/pois${qs ? `?${qs}` : ""}`);
  },
  poi: (slug, poiSlug) =>
    request(`/venues/${encodeURIComponent(slug)}/pois/${encodeURIComponent(poiSlug)}`),
  // AI assistant (item 0325): { chip } or { q }, answered from this venue only.
  assistant: (slug, body) =>
    request(`/venues/${encodeURIComponent(slug)}/assistant`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  route: (slug, { fromKiosk, toPoi }) => {
    const params = new URLSearchParams({ from_kiosk: fromKiosk, to_poi: toPoi });
    return request(`/venues/${encodeURIComponent(slug)}/route?${params}`);
  },
};

export const imageUrl = (name) => (name ? `${BASE}/images/${name}` : null);

/** A POI clip (item 0323). Content-hash names, served with Range support. */
export const videoUrl = (name) => (name ? `${BASE}/videos/${encodeURIComponent(name)}` : null);

/** URL of a .glb in the 3D model library (built-in or venue-uploaded).
 *  `version` comes from the catalogue and changes when a built-in model is
 *  regenerated — without it a browser that cached the old mesh would keep it. */
export const modelUrl = (name, version) =>
  name
    ? `${BASE}/models/${encodeURIComponent(name)}.glb${version ? `?v=${encodeURIComponent(version)}` : ""}`
    : null;

/** The models one venue places, with their cache versions.
 *  Replaces a global list of every upload on the server: that response was
 *  unauthenticated and enumerated other customers' models (item 0097). */
export const listVenueModels = (slug) =>
  request(`/venues/${encodeURIComponent(slug)}/models`);

export { ApiError };
